/* eslint-disable max-lines */
import { authenticatedFetch } from "@lumi/data";
import { AlertTriangle, ArrowUpRight, BarChart3, Play, Timer, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { createRefreshCallback } from "../../auth/refreshCallback";
import {
  AnalyticsStatCard,
  FunnelCard,
  LineChartCard,
  MiniProgressBar,
  MultiSeriesAreaChartCard,
  PieChartCard,
  RadialBarChartCard,
} from "../../shared/components/charts";
import type { ChartConfig } from "../../shared/components/ui/chart";
import { cn } from "../../shared/components/ui/cn";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../shared/components/ui/select";
import { useCascadeFadeIn } from "../../shared/hooks/useCascadeFadeIn";
import type {
  ChurnPrecursorsResponse,
  EngagementPatternsResponse,
  RetentionCohortsResponse,
  TelemetryHealthResponse,
} from "./analytics-v2-types";
interface RealTimeStats {
  activeViewers: number;
  avgSessionTime: number;
  topTitle: string;
  topTitleViews: number;
}

interface ContentMetric {
  title: string;
  view_count: number;
  completion_rate: number;
}

interface TelemetrySummary {
  navigation_events: number;
  playback_events: number;
  total_events: number;
  unique_sessions: number;
  unique_users: number;
}

interface ConversionFunnelStage {
  stage: string;
  value: number;
  conversion_rate: number;
}

interface EngagementMetrics {
  avgSessionMinutes: number;
  medianSessionMinutes: number;
  totalSessions: number;
  uniqueUsers: number;
  returnRate: number;
  hourlyViewers: Array<{ time: string; viewers: number }>;
}

interface ContentDiscovery {
  topClickedTitles: Array<{
    title: string;
    clicks: number;
    uniqueUsers: number;
    plays: number;
    clickToPlayRate: number;
  }>;
  topPages: Array<{
    page: string;
    visits: number;
    uniqueSessions: number;
  }>;
}

interface DropoffAnalysis {
  dropoffCurve: Array<{
    range: string;
    count: number;
  }>;
  titleRetention: Array<{
    title: string;
    avgWatchPercentage: number;
    views: number;
  }>;
}

// Chart color palette
const CHART_COLORS = {
  golden: "hsl(43, 87%, 60%)",
  green: "hsl(142, 76%, 45%)",
  blue: "hsl(217, 91%, 60%)",
} as const;

/**
 * Returns the appropriate text color class based on completeness rate thresholds.
 */
function getCompletenessColorClass(rate: number): string {
  if (rate >= 80) return "text-green-400";
  if (rate >= 60) return "text-amber-400";
  return "text-red-400";
}

const EVENT_CHART_CONFIG: ChartConfig = {
  events: { label: "Events" },
  navigation: { label: "Navigation", color: CHART_COLORS.golden },
  playback: { label: "Playback", color: CHART_COLORS.green },
};

/**
 * Animated wrapper for chart components
 */
function AnimatedChartWrapper({
  children,
  index,
  className,
}: {
  readonly children: React.ReactNode;
  readonly index: number;
  readonly className?: string;
}) {
  const { ref, isVisible } = useCascadeFadeIn({ index, threshold: 0.1, staggerDelay: 80 });

  return (
    <div
      ref={ref}
      className={cn(
        "transition-all duration-500 ease-out h-full",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

/* eslint-disable max-lines-per-function */
export function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState("7d");
  const [error, setError] = useState<string | null>(null);
  const [realTimeStats, setRealTimeStats] = useState<RealTimeStats | null>(null);
  const [contentMetrics, setContentMetrics] = useState<ContentMetric[]>([]);
  const [telemetrySummary, setTelemetrySummary] = useState<TelemetrySummary | null>(null);
  const [conversionFunnel, setConversionFunnel] = useState<ConversionFunnelStage[] | null>(null);
  const [engagementMetrics, setEngagementMetrics] = useState<EngagementMetrics | null>(null);
  const [contentDiscovery, setContentDiscovery] = useState<ContentDiscovery | null>(null);
  const [dropoffAnalysis, setDropoffAnalysis] = useState<DropoffAnalysis | null>(null);

  // TELEM-03 Phase 1 analytics state
  const [retentionCohorts, setRetentionCohorts] = useState<RetentionCohortsResponse | null>(null);
  const [engagementPatterns, setEngagementPatterns] = useState<EngagementPatternsResponse | null>(null);
  const [churnPrecursors, setChurnPrecursors] = useState<ChurnPrecursorsResponse | null>(null);
  const [telemetryHealth, setTelemetryHealth] = useState<TelemetryHealthResponse | null>(null);

  const { accessToken, refreshAuth } = useAuth();

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  // Load real analytics data
  useEffect(() => {
    const loadData = async () => {
      if (!accessToken) {
        return;
      }

      try {
        const refreshCallback = createRefreshCallback(refreshAuth);

        // Fetch all analytics data in parallel
        // Fetch legacy analytics (v1) and new TELEM-03 analytics (v2) in parallel
        const [
          statsRes,
          contentRes,
          telemetryRes,
          funnelRes,
          engagementRes,
          discoveryRes,
          dropoffRes,
          // TELEM-03 Phase 1 endpoints
          retentionRes,
          patternsRes,
          churnRes,
          healthRes,
        ] = await Promise.all([
          // Legacy v1 endpoints
          authenticatedFetch<RealTimeStats>(
            `${API_BASE}/v1/analytics/real-time-stats`,
            { method: "GET" },
            accessToken,
            refreshCallback,
          ),
          authenticatedFetch<ContentMetric[]>(
            `${API_BASE}/v1/analytics/content-performance?timeRange=${timeRange}`,
            { method: "GET" },
            accessToken,
            refreshCallback,
          ),
          authenticatedFetch<TelemetrySummary>(
            `${API_BASE}/v1/analytics/telemetry-summary`,
            { method: "GET" },
            accessToken,
            refreshCallback,
          ),
          authenticatedFetch<ConversionFunnelStage[]>(
            `${API_BASE}/v1/analytics/conversion-funnel?timeRange=${timeRange}`,
            { method: "GET" },
            accessToken,
            refreshCallback,
          ),
          authenticatedFetch<EngagementMetrics>(
            `${API_BASE}/v1/analytics/engagement-metrics?timeRange=${timeRange}`,
            { method: "GET" },
            accessToken,
            refreshCallback,
          ),
          authenticatedFetch<ContentDiscovery>(
            `${API_BASE}/v1/analytics/content-discovery?timeRange=${timeRange}`,
            { method: "GET" },
            accessToken,
            refreshCallback,
          ),
          authenticatedFetch<DropoffAnalysis>(
            `${API_BASE}/v1/analytics/dropoff-analysis?timeRange=${timeRange}`,
            { method: "GET" },
            accessToken,
            refreshCallback,
          ),
          // TELEM-03 Phase 1 endpoints (require ADMIN role)
          authenticatedFetch<RetentionCohortsResponse>(
            `${API_BASE}/v1/analytics/retention/cohorts?limit=30`,
            { method: "GET" },
            accessToken,
            refreshCallback,
          ).catch(() => null), // Graceful fallback if not ADMIN
          authenticatedFetch<EngagementPatternsResponse>(
            `${API_BASE}/v1/analytics/engagement/patterns?weeks=4`,
            { method: "GET" },
            accessToken,
            refreshCallback,
          ).catch(() => null),
          authenticatedFetch<ChurnPrecursorsResponse>(
            `${API_BASE}/v1/analytics/churn/precursors?lookbackWeeks=8&declineThreshold=30`,
            { method: "GET" },
            accessToken,
            refreshCallback,
          ).catch(() => null),
          authenticatedFetch<TelemetryHealthResponse>(
            `${API_BASE}/v1/analytics/telemetry/health?days=7`,
            { method: "GET" },
            accessToken,
            refreshCallback,
          ).catch(() => null),
        ]);

        setRealTimeStats(statsRes);
        setContentMetrics(contentRes);
        setTelemetrySummary(telemetryRes);
        setConversionFunnel(funnelRes);
        setEngagementMetrics(engagementRes);
        setContentDiscovery(discoveryRes);
        setDropoffAnalysis(dropoffRes);

        // TELEM-03 Phase 1 state updates
        setRetentionCohorts(retentionRes);
        setEngagementPatterns(patternsRes);
        setChurnPrecursors(churnRes);
        setTelemetryHealth(healthRes);

        setError(null); // Clear any previous errors
      } catch (error) {
        console.error("Failed to load analytics data:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to load analytics data";
        setError(errorMessage);
        // Fall back to empty data on error
        setRealTimeStats({
          activeViewers: 0,
          avgSessionTime: 0,
          topTitle: "No data",
          topTitleViews: 0,
        });
        setContentMetrics([]);
        setTelemetrySummary({
          navigation_events: 0,
          playback_events: 0,
          total_events: 0,
          unique_sessions: 0,
          unique_users: 0,
        });
        setConversionFunnel([]);
      }
    };

    void loadData();
  }, [timeRange, accessToken, refreshAuth, API_BASE]);

  return (
    <div className="space-y-8 py-8">
      {/* Compact Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Analytics Dashboard</h1>
          <p className="text-sm text-neutral-400 pb-6">View all system activity and user actions</p>
        </div>
        <Select value={timeRange} onValueChange={(value) => setTimeRange(value)}>
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <span>
              {timeRange === "1d" && "Last 24h"}
              {timeRange === "7d" && "Last 7 days"}
              {timeRange === "30d" && "Last 30 days"}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1d">Last 24h</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="text-red-400 mt-0.5">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-red-300 font-medium text-sm">Analytics Error</div>
              <div className="text-red-200/80 text-sm mt-1">{error}</div>
              <button
                onClick={() => globalThis.location.reload()}
                className="text-red-400 text-sm underline mt-2 hover:text-red-300"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compact Top Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <AnimatedChartWrapper index={0}>
          <AnalyticsStatCard
            label="Active Viewers"
            value={realTimeStats?.activeViewers.toString() || "0"}
            subtitle="Currently watching"
            trend={12}
            compact
          />
        </AnimatedChartWrapper>
        <AnimatedChartWrapper index={1}>
          <AnalyticsStatCard
            label="Avg Session"
            value={`${realTimeStats?.avgSessionTime || 0}m`}
            subtitle="Last 24 hours"
            trend={-3}
            compact
          />
        </AnimatedChartWrapper>
        <AnimatedChartWrapper index={2}>
          <AnalyticsStatCard
            label="Total Events"
            value={(telemetrySummary?.total_events || 0).toLocaleString()}
            subtitle={`${telemetrySummary?.unique_users || 0} users`}
            trend={8}
            compact
          />
        </AnimatedChartWrapper>
        <AnimatedChartWrapper index={3}>
          <AnalyticsStatCard
            label="Conversion"
            value={`${engagementMetrics?.returnRate || 0}%`}
            subtitle="Return rate"
            trend={5}
            compact
          />
        </AnimatedChartWrapper>
      </div>

      {/* Charts - Compact layout */}
      <div className="space-y-4">
        {/* Two-column: Performance trend + Pie/Funnel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <AnimatedChartWrapper index={4}>
              <MultiSeriesAreaChartCard
                className="h-full"
                title="Content Performance"
                description="Views per title - last 7 days"
                data={Array.from({ length: 7 }, (_, dayIndex) => {
                  const date = new Date();
                  date.setDate(date.getDate() - (6 - dayIndex));
                  const dayLabel = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

                  const dayData: Record<string, string | number> = { date: dayLabel };

                  for (const metric of contentMetrics) {
                    const avgDaily = metric.view_count / 7;
                    const dayOfWeek = date.getDay();
                    const weekendFactor = dayOfWeek === 0 || dayOfWeek === 6 ? 0.7 : 1;
                    const midWeekBoost = dayOfWeek >= 2 && dayOfWeek <= 4 ? 1.2 : 1;
                    dayData[metric.title] = Math.round(avgDaily * weekendFactor * midWeekBoost);
                  }

                  return dayData;
                })}
                series={contentMetrics.slice(0, 4).map((metric, idx) => ({
                  key: metric.title,
                  label: metric.title,
                  color:
                    [CHART_COLORS.golden, CHART_COLORS.green, CHART_COLORS.blue, "hsl(258, 90%, 66%)"][idx % 4] ||
                    CHART_COLORS.golden,
                }))}
                xAxisKey="date"
                footerLabel="days"
                trendPercent={12}
              />
            </AnimatedChartWrapper>
          </div>
          <AnimatedChartWrapper index={5}>
            <PieChartCard
              className="h-full"
              title="Event Distribution"
              description="Navigation vs Playback"
              data={
                telemetrySummary
                  ? [
                      {
                        category: "navigation",
                        value: telemetrySummary.navigation_events,
                        fill: "var(--color-navigation)",
                      },
                      { category: "playback", value: telemetrySummary.playback_events, fill: "var(--color-playback)" },
                    ]
                  : [
                      { category: "navigation", value: 0, fill: "var(--color-navigation)" },
                      { category: "playback", value: 0, fill: "var(--color-playback)" },
                    ]
              }
              chartConfig={EVENT_CHART_CONFIG}
              totalLabel="Events"
              footerText={`${telemetrySummary?.unique_users.toLocaleString() || 0} users`}
            />
          </AnimatedChartWrapper>
        </div>

        {/* Funnel + Hourly Viewers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AnimatedChartWrapper index={6}>
            <FunnelCard
              className="h-full"
              title="User Journey"
              description="Conversion funnel"
              data={conversionFunnel}
            />
          </AnimatedChartWrapper>
          {engagementMetrics && engagementMetrics.hourlyViewers.length > 0 && (
            <AnimatedChartWrapper index={7}>
              <LineChartCard
                className="h-full"
                title="Viewers Over Time"
                description="Hourly unique viewers"
                data={engagementMetrics.hourlyViewers.map((h) => ({
                  time: new Date(h.time).toLocaleTimeString("en-US", { hour: "numeric", hour12: true }),
                  viewers: h.viewers,
                }))}
                valueKey="viewers"
                valueLabel="Viewers"
                nameKey="time"
                color={CHART_COLORS.blue}
                footerMetric="Peak"
                footerValue={Math.max(...engagementMetrics.hourlyViewers.map((h) => h.viewers))}
              />
            </AnimatedChartWrapper>
          )}
        </div>

        {/* Compact Two-Column Layout: Engagement Stats + Content Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Engagement Quick Stats */}
          {engagementMetrics && (
            <AnimatedChartWrapper index={8}>
              <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 h-full">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-white">User Engagement</h3>
                  <Timer className="w-4 h-4 text-neutral-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-2xl font-bold text-white">{engagementMetrics.avgSessionMinutes}m</div>
                    <div className="text-[10px] text-neutral-500">Avg Session</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">
                      {engagementMetrics.totalSessions.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-neutral-500">Total Sessions</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-green-400">{engagementMetrics.returnRate}%</div>
                    <div className="text-[10px] text-neutral-500">Return Rate</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-lumi-accent">
                      {engagementMetrics.uniqueUsers.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-neutral-500">Unique Users</div>
                  </div>
                </div>
              </div>
            </AnimatedChartWrapper>
          )}

          {/* Middle: Content Completion Progress Bars */}
          <AnimatedChartWrapper index={9}>
            <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-white">Completion Rates</h3>
                <BarChart3 className="w-4 h-4 text-neutral-500" />
              </div>
              <div className="space-y-2.5">
                {contentMetrics.slice(0, 5).map((metric, idx) => (
                  <MiniProgressBar
                    key={metric.title}
                    label={metric.title.length > 12 ? metric.title.slice(0, 12) + "..." : metric.title}
                    value={Math.round(metric.completion_rate || 0)}
                    color={
                      ["golden", "green", "blue", "amber", "red"][idx % 5] as
                        | "golden"
                        | "green"
                        | "blue"
                        | "amber"
                        | "red"
                    }
                  />
                ))}
              </div>
            </div>
          </AnimatedChartWrapper>

          {/* Right: Top Content Quick List */}
          <AnimatedChartWrapper index={10}>
            <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-white">Top Content</h3>
                <Play className="w-4 h-4 text-neutral-500" />
              </div>
              <div className="space-y-2">
                {contentMetrics.slice(0, 5).map((metric, idx) => (
                  <div key={metric.title} className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] font-medium text-neutral-500 w-4">{idx + 1}</span>
                      <span className="text-xs text-neutral-300 truncate">{metric.title}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className="text-xs font-medium text-white">{metric.view_count}</span>
                      <ArrowUpRight className="w-3 h-3 text-green-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedChartWrapper>
        </div>

        {/* Content Discovery + Retention: 3-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {contentDiscovery && contentDiscovery.topPages.length > 0 && (
            <AnimatedChartWrapper index={12}>
              <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 h-full">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-white">Top Pages</h3>
                  <Users className="w-4 h-4 text-neutral-500" />
                </div>
                <div className="space-y-2">
                  {contentDiscovery.topPages.slice(0, 5).map((p, idx) => (
                    <div key={p.page} className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] font-medium text-neutral-500 w-4">{idx + 1}</span>
                        <span className="text-xs text-neutral-300 truncate">
                          {p.page.replace(/^\//, "").split("/").slice(-2).join("/") || "Home"}
                        </span>
                      </div>
                      <span className="text-xs font-medium text-white flex-shrink-0">{p.visits}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedChartWrapper>
          )}
          {dropoffAnalysis && dropoffAnalysis.dropoffCurve.length > 0 && (
            <AnimatedChartWrapper index={15}>
              <PieChartCard
                className="h-full"
                title="Abandonment Distribution"
                description="When viewers stop watching"
                data={dropoffAnalysis.dropoffCurve.map((d, idx) => ({
                  category: d.range,
                  value: d.count,
                  fill: `hsl(${idx * 15}, 70%, ${50 - idx * 3}%)`, // Red to orange gradient
                }))}
                chartConfig={{
                  "0-10%": { label: "0-10%", color: "hsl(0, 70%, 55%)" },
                  "10-25%": { label: "10-25%", color: "hsl(15, 70%, 52%)" },
                  "25-50%": { label: "25-50%", color: "hsl(30, 70%, 49%)" },
                  "50-75%": { label: "50-75%", color: "hsl(45, 70%, 46%)" },
                  "75-90%": { label: "75-90%", color: "hsl(60, 70%, 43%)" },
                  "90-100%": { label: "90-100%", color: "hsl(75, 70%, 40%)" },
                }}
                totalLabel="Abandonments"
                footerText={`${dropoffAnalysis.dropoffCurve.reduce((sum, d) => sum + d.count, 0).toLocaleString()} total`}
              />
            </AnimatedChartWrapper>
          )}
          {dropoffAnalysis && dropoffAnalysis.titleRetention.length > 0 && (
            <AnimatedChartWrapper index={14}>
              <RadialBarChartCard
                className="h-full"
                title="Content Retention"
                description="Watch % by title"
                data={dropoffAnalysis.titleRetention.slice(0, 4).map((t, idx) => ({
                  name: t.title,
                  value: t.avgWatchPercentage,
                  fill: `hsl(${120 - idx * 20}, 70%, ${45 + idx * 8}%)`,
                }))}
                chartConfig={{
                  value: { label: "Retention", color: CHART_COLORS.green },
                }}
                footerLabel="titles"
                valueLabel="retention"
              />
            </AnimatedChartWrapper>
          )}
        </div>

        {/* ================================================================== */}
        {/* TELEM-03 Phase 1: Advanced Analytics - Compact Section            */}
        {/* ================================================================== */}

        {(retentionCohorts || engagementPatterns || churnPrecursors || telemetryHealth) && (
          <>
            <div className="mt-6 mb-4 border-t border-neutral-700 pt-4">
              <h2 className="text-lg font-bold text-white">Advanced Analytics</h2>
            </div>

            {/* Row 1: Retention Cohorts + Engagement Pie */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {retentionCohorts && retentionCohorts.data.length > 0 && (
                <AnimatedChartWrapper index={15}>
                  <MultiSeriesAreaChartCard
                    className="h-full"
                    title="Retention Cohorts"
                    description="Day 1/7/30 return rates"
                    data={retentionCohorts.data.slice(0, 10).map((cohort) => ({
                      date: new Date(cohort.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
                      D1: Math.round(cohort.day1Retention),
                      D7: Math.round(cohort.day7Retention),
                      D30: Math.round(cohort.day30Retention),
                    }))}
                    series={[
                      { key: "D1", label: "Day 1", color: CHART_COLORS.golden },
                      { key: "D7", label: "Day 7", color: CHART_COLORS.green },
                      { key: "D30", label: "Day 30", color: CHART_COLORS.blue },
                    ]}
                    xAxisKey="date"
                    footerLabel="cohorts"
                    trendPercent={
                      retentionCohorts.data.length > 1
                        ? Math.round(retentionCohorts.data[0].day7Retention - retentionCohorts.data[1].day7Retention)
                        : 0
                    }
                  />
                </AnimatedChartWrapper>
              )}
              {engagementPatterns && (
                <AnimatedChartWrapper index={16}>
                  <PieChartCard
                    className="h-full"
                    title="Engagement Types"
                    description="Viewer classification"
                    data={[
                      {
                        category: "Binge",
                        value: engagementPatterns.data.summary.bingeViewers,
                        fill: CHART_COLORS.golden,
                      },
                      {
                        category: "Regular",
                        value: engagementPatterns.data.summary.regularViewers,
                        fill: CHART_COLORS.green,
                      },
                      {
                        category: "Casual",
                        value: engagementPatterns.data.summary.casualViewers,
                        fill: CHART_COLORS.blue,
                      },
                    ]}
                    chartConfig={{
                      Binge: { label: "Binge", color: CHART_COLORS.golden },
                      Regular: { label: "Regular", color: CHART_COLORS.green },
                      Casual: { label: "Casual", color: CHART_COLORS.blue },
                    }}
                    totalLabel="Users"
                    footerText={`${engagementPatterns.data.summary.totalActiveUsers.toLocaleString()} active`}
                  />
                </AnimatedChartWrapper>
              )}
            </div>

            {/* Row 2: Compact Engagement Stats + Churn + Health */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {engagementPatterns && (
                <>
                  <AnimatedChartWrapper index={17}>
                    <AnalyticsStatCard
                      label="Binge"
                      value={`${Math.round(engagementPatterns.data.summary.bingePercent)}%`}
                      subtitle={`${engagementPatterns.data.summary.bingeViewers} users`}
                      trend={5}
                      compact
                    />
                  </AnimatedChartWrapper>
                  <AnimatedChartWrapper index={18}>
                    <AnalyticsStatCard
                      label="Regular"
                      value={`${Math.round(engagementPatterns.data.summary.regularPercent)}%`}
                      subtitle={`${engagementPatterns.data.summary.regularViewers} users`}
                      compact
                    />
                  </AnimatedChartWrapper>
                </>
              )}
              {churnPrecursors && (
                <AnimatedChartWrapper index={19}>
                  <AnalyticsStatCard
                    label="At Risk"
                    value={churnPrecursors.data.summary.decliningUsers.toString()}
                    subtitle={`${Math.round(churnPrecursors.data.summary.decliningPercent)}% declining`}
                    trend={-Math.round(churnPrecursors.data.summary.decliningPercent)}
                    compact
                  />
                </AnimatedChartWrapper>
              )}
              {telemetryHealth && (
                <AnimatedChartWrapper index={20}>
                  <AnalyticsStatCard
                    label="Telemetry"
                    value={`${telemetryHealth.data.overall.completenessRate}%`}
                    subtitle="Completeness"
                    trend={telemetryHealth.data.overall.completenessRate >= 80 ? 0 : -5}
                    compact
                  />
                </AnimatedChartWrapper>
              )}
            </div>

            {/* Row 3: Compact Churn Table + Telemetry Health Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Churn Risk - Compact Table */}
              {churnPrecursors && churnPrecursors.data.atRiskUsers.length > 0 && (
                <AnimatedChartWrapper index={21}>
                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 h-full flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        <h3 className="text-sm font-medium text-white">At-Risk Users</h3>
                      </div>
                      <span className="text-xs text-amber-400 font-medium">
                        {churnPrecursors.data.summary.decliningUsers} declining
                      </span>
                    </div>
                    <div className="space-y-1.5 flex-1">
                      {churnPrecursors.data.atRiskUsers.slice(0, 5).map((user) => (
                        <div key={user.userId} className="flex items-center justify-between py-1 text-xs">
                          <span className="text-neutral-400 font-mono">{user.userId.slice(0, 8)}...</span>
                          <div className="flex items-center gap-3">
                            <span className="text-red-400">{user.sessionDecline}%</span>
                            <span className="text-neutral-500">{user.daysSinceActive}d</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-2 border-t border-neutral-800 flex justify-between text-[10px]">
                      <span className="text-red-400">{churnPrecursors.data.summary.decliningUsers} declining</span>
                      <span className="text-neutral-500">{churnPrecursors.data.summary.stableUsers} stable</span>
                      <span className="text-green-400">{churnPrecursors.data.summary.growingUsers} growing</span>
                    </div>
                  </div>
                </AnimatedChartWrapper>
              )}

              {/* Telemetry Health - Compact Grid */}
              {telemetryHealth && (
                <AnimatedChartWrapper index={22}>
                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 h-full flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-white">Platform Health</h3>
                      <span
                        className={cn(
                          "text-xs font-medium",
                          telemetryHealth.data.overall.completenessRate >= 80 ? "text-green-400" : "text-amber-400",
                        )}
                      >
                        {telemetryHealth.data.overall.completenessRate}% complete
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 flex-1">
                      {telemetryHealth.data.byPlatformDevice.slice(0, 4).map((item) => (
                        <div
                          key={`${item.platform}-${item.deviceType}`}
                          className="bg-neutral-800/50 rounded px-3 py-2"
                        >
                          <div className="text-[10px] text-neutral-500 uppercase">
                            {item.platform}/{item.deviceType}
                          </div>
                          <div
                            className={cn("text-sm font-semibold", getCompletenessColorClass(item.completenessRate))}
                          >
                            {item.completenessRate}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </AnimatedChartWrapper>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
